/*
 * <rs-blog-nav> — interaction only for the blog pillar nav dropdown.
 *
 * This element does NOT show, hide, position or animate anything. All of that
 * is CSS, keyed off `aria-expanded` on the trigger plus the `js` / `no-js`
 * class the theme sets on <html> from a synchronous head script. The markup
 * ships in the closed state, so nothing changes at mount and there is no
 * first-load flash to work around.
 *
 * What is left is genuinely behavioural: toggling the attribute, and closing on
 * Escape / outside click / focus leaving the component.
 *
 * This is a DISCLOSURE, not a listbox. Activating an entry navigates rather
 * than selecting a value, so the entries stay plain links and there is no
 * roving tabindex or arrow-key handling — Tab moves through them, which is
 * what a screen reader user expects from a set of links.
 */
class RsBlogNav extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector('.rs-blog-nav__trigger');
    this.list = this.querySelector('.rs-blog-nav__list');
    if (!this.trigger || !this.list) return;

    // Matches the breakpoint in rs-blog-nav.css. Above it the trigger is
    // display:none, so there is no dropdown to manage.
    this.query = window.matchMedia('(max-width: 1023px)');

    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onFocusOut = this.onFocusOut.bind(this);
    this.onBreakpointChange = this.onBreakpointChange.bind(this);

    this.trigger.addEventListener('click', this.onTriggerClick);
    /*
     * keyup, not keydown. cart-drawer.js binds a document-level keyup that
     * fires on EVERY Escape whether or not the drawer is open, and its close()
     * calls previousElement.focus() — which drags focus to the cart toggle.
     * Handling the same event type lets stopPropagation below keep the event
     * from ever reaching that listener. On keydown it would still leak.
     */
    this.addEventListener('keyup', this.onKeyUp);
    this.addEventListener('focusout', this.onFocusOut);
    document.addEventListener('click', this.onDocumentClick);
    this.query.addEventListener('change', this.onBreakpointChange);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    if (this.query) this.query.removeEventListener('change', this.onBreakpointChange);
  }

  get isOpen() {
    return this.trigger.getAttribute('aria-expanded') === 'true';
  }

  open() {
    this.trigger.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.trigger.setAttribute('aria-expanded', 'false');
  }

  onTriggerClick() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  /*
   * Crossing up to the pill row leaves aria-expanded="true" behind if the menu
   * was open. CSS ignores it at that width, but the attribute would then be
   * lying to assistive tech, and coming back down would render the menu open.
   */
  onBreakpointChange() {
    if (!this.query.matches && this.isOpen) this.close();
  }

  onKeyUp(event) {
    if (event.key !== 'Escape' || !this.isOpen || !this.query.matches) return;

    /*
     * Order matters. Closing first hides the link that currently has focus,
     * which drops focus to the document and leaves it there. Moving focus to
     * the trigger before hiding anything avoids that window entirely.
     *
     * The event only reaches this handler when focus is already inside the
     * nav, so consuming it cannot swallow an Escape meant for the cart drawer
     * or any other overlay — those would have focus themselves.
     */
    this.trigger.focus();
    this.close();
    event.stopPropagation();
  }

  onDocumentClick(event) {
    if (!this.query.matches || !this.isOpen) return;
    if (this.contains(event.target)) return;
    this.close();
  }

  /*
   * Tabbing past the last link leaves an open panel behind with nothing
   * focused inside it. relatedTarget is where focus is heading; null means it
   * left the document entirely, which is not a reason to close.
   */
  onFocusOut(event) {
    if (!this.query.matches || !this.isOpen) return;
    if (!event.relatedTarget || this.contains(event.relatedTarget)) return;
    this.close();
  }
}

if (!customElements.get('rs-blog-nav')) {
  customElements.define('rs-blog-nav', RsBlogNav);
}
